import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TableFooterProps, TableRowData } from '../types';
import { ResultsIndicator } from './ResultsIndicator';

function getPageNumbers(
  totalPages: number,
  currentPage: number,
  maxVisible = 5
): number[] {
  if (totalPages <= 0) {
    return [];
  }

  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const half = Math.floor(maxVisible / 2);
  const start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + maxVisible - 1);
  const adjustedStart = Math.max(1, end - maxVisible + 1);

  return Array.from({ length: end - adjustedStart + 1 }, (_, index) => {
    return adjustedStart + index;
  });
}

/**
 * Table footer with pagination controls
 * Uses context to access table instance and query state
 */
export function TableFooter<TRow extends TableRowData>({
  pageSizeOptions,
  table,
  paginationState,
  onPageChange,
  onPageSizeChange,
}: TableFooterProps<TRow>) {
  const pageIndex = paginationState.pageIndex;
  const pageSize = paginationState.pageSize;
  const totalPages = table.getPageCount();
  const currentPage = pageIndex + 1;
  const pageNumbers = getPageNumbers(totalPages, currentPage);

  return (
    <div className="flex items-center bg-card justify-between px-4 py-2 border-t">
      <ResultsIndicator
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRows={table.getRowCount()}
      />
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(0)}
          disabled={!table.getCanPreviousPage()}
          className="h-8 w-8 p-0"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
          disabled={!table.getCanPreviousPage()}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPageChange(page - 1)}
            className="h-8 w-8 p-0"
          >
            {page}
          </Button>
        ))}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={!table.getCanNextPage()}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={!table.getCanNextPage()}
          className="h-8 w-8 p-0"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>

        <select
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          className="ml-4 rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
        >
          {pageSizeOptions.map(size => (
            <option
              key={size}
              value={size}
              className="bg-background text-foreground"
            >
              {size} per page
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
